import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import mixpanel from "mixpanel-browser";
import { useDispatch, useSelector } from "react-redux";
import { AuthoritiesEnum, IntegrationTypeEnum } from "~/types/enums";
import { Alert, Card, Grid, Icon, InputCheckbox, InputToggle, Picture, Spinner, Text } from "~/components-shared";
import integrationsActionCreators from "~/store/integrations/actions";
import integrationSelectors from "~/store/integrations/selectors";
import useAbacExt from "~/hooks/abac";
import api from "~/lib/api";
import jiraLogo from "~/assets/i/apps/atlassian_jira_logo.svg";
import { handleError, helpers } from "~/lib/helpers";
import styled from "styled-components";
const Actions = styled.div`
  position: relative;
  z-index: 2;
`;
const StyledGrid = styled(Grid)`
  position: relative;
  z-index: 2;
  max-height: 250px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`;
const JiraIntegrationCard = _ref => {
  let {
    ...rest
  } = _ref;
  const {
    userHasOneOf
  } = useAbacExt();
  const dispatch = useDispatch();
  const integration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.JIRA));
  const [jiraProjects, setJiraProjects] = useState([]);
  const [loadingJiraProjects, setLoadingJiraProjects] = useState(false);
  const [jiraProjectsError, setJiraProjectsError] = useState();
  const [isExpanded, setIsExpanded] = useState(false);
  const handleLoadJiraProjects = () => {
    setLoadingJiraProjects(true);
    setJiraProjectsError(undefined);
    api.fetchJiraProjects().then(data => setJiraProjects(data)).catch(e => setJiraProjectsError(handleError(e))).finally(() => setLoadingJiraProjects(false));
  };
  useEffect(() => {
    if (integration && userHasOneOf([AuthoritiesEnum.INTEGRATIONS_WRITE, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE])) {
      handleLoadJiraProjects();
    }
  }, [!!integration]);
  const handleToggle = value => {
    if (value) {
      api.jiraInstall().then(_ref2 => {
        let {
          path
        } = _ref2;
        const width = 560;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;
        window.open(path, "Install Jira", `width=${width},height=${height},left=${left},top=${top}`);
      });
      setIsExpanded(true);
      mixpanel.track(`Jira Integration – Install`);
    } else if (integration?.id) {
      dispatch(integrationsActionCreators.deleteStarted(integration.id));
      mixpanel.track(`Jira Integration – Uninstall`);
    }
  };
  const handleExpand = () => {
    setIsExpanded(prev => !prev);
  };
  const handleUpdate = projectId => {
    if (integration) {
      let projects = integration.settings?.projects || [];
      if (projects.includes(projectId)) {
        projects = projects.filter(id => id !== projectId);
      } else {
        projects.push(projectId);
      }
      dispatch(integrationsActionCreators.updateStarted(integration.id, {
        type: integration.type,
        token: integration.token,
        settings: {
          ...integration.settings,
          projects
        }
      }));
      mixpanel.track(`Jira Integration – Update`, {
        ...helpers.objectDiff({
          ...integration.settings
        }, {
          ...integration.settings,
          projects
        })
      });
    }
  };
  return /*#__PURE__*/React.createElement(Card, _extends({
    onClick: userHasOneOf([AuthoritiesEnum.INTEGRATIONS_WRITE, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE]) && integration ? handleExpand : undefined,
    elemBefore: /*#__PURE__*/React.createElement(Picture, {
      src: jiraLogo,
      width: "30px",
      height: "30px",
      border: "squared",
      color: "transparent",
      s: {
        mr: 2
      }
    }),
    title: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      element: "h3",
      display: "inline-block"
    }, "Atlassian Jira"), integration && userHasOneOf([AuthoritiesEnum.INTEGRATIONS_WRITE, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE]) && /*#__PURE__*/React.createElement(Icon, {
      scale: 4,
      src: "chevron",
      rotate: isExpanded ? 0 : 180,
      s: {
        ml: 1,
        mb: 1
      }
    })),
    elemAfter: /*#__PURE__*/React.createElement(Actions, null, /*#__PURE__*/React.createElement(InputToggle, {
      value: !!integration,
      display: "inline-block",
      onChange: (value, event) => {
        event.stopPropagation();
        handleToggle(value);
      },
      disabled: !userHasOneOf([AuthoritiesEnum.INTEGRATIONS_WRITE, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE])
    })),
    collapsed: !integration || !isExpanded,
    s: {
      mb: 3
    }
  }, rest), jiraProjectsError && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, jiraProjectsError), loadingJiraProjects ? /*#__PURE__*/React.createElement(Spinner, {
    center: true
  }) : /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 2,
    template: "1rem 3rem 1fr",
    s: {
      pl: 2
    },
    gap: 3,
    justify: "center"
  }, jiraProjects.map(project => /*#__PURE__*/React.createElement(React.Fragment, {
    key: project.id
  }, /*#__PURE__*/React.createElement(InputCheckbox, {
    value: integration?.settings?.projects?.includes(project.key),
    onChange: () => handleUpdate(project.key),
    s: {
      mt: 1
    }
  }), /*#__PURE__*/React.createElement(Picture, {
    width: "3rem",
    height: "3rem",
    src: project.avatar
  }), /*#__PURE__*/React.createElement(Grid.Cell, {
    s: {
      m: 0
    }
  }, /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "semiBold"
  }, project.name, " (", project.key, ")"), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "semiBold",
    color: "textSecondary"
  }, project.description || "No description"))))));
};
export default JiraIntegrationCard;